import React, { useState, useEffect } from 'react';
import { 
  Container,
  TextField,
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Divider,
  IconButton,
  Modal,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Public,
  Delete,
  Check,
  Close,
  Edit,
  FavoriteBorder,
  Bookmark,
  Launch as LaunchIcon,
  Favorite,     
} from '@mui/icons-material';
import Layout from '../components/Layout';
import LoadingAnimation from '../components/LoadingAnimation';
import { jwtDecode } from 'jwt-decode';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AvatarIcon from '../avatar.png';
import { Switch, FormControlLabel } from '@mui/material';

const MySaves = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [links, setLinks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userLikes, setUserLikes] = useState({});
  const [currentUserId, setCurrentUserId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editedCaption, setEditedCaption] = useState('');
  const [likesModal, setLikesModal] = useState({
    open: false,
    postId: null,
    likes: []
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [userSaves, setUserSaves] = useState({});
  const [viewMode, setViewMode] = useState('mySaves'); // 'mySaves' or 'otherUsers'

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const { uid } = jwtDecode(token);
        setCurrentUserId(uid);        
                
        const likesResponse = await fetch(`https://kast-server-713b1c89d478.herokuapp.com/api/auth/likes/${uid}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const likesData = await likesResponse.json();
        setUserLikes(likesData.likes || {});
        
        // Fetch user's saved posts
        const savedPostsResponse = await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/saved-posts', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const savedPostsData = await savedPostsResponse.json();
        const savedPosts = savedPostsData.posts || [];
        
        // Create saves map for quick lookup
        const savesMap = savedPosts.reduce((acc, post) => {
          acc[post.id] = true;
          return acc;
        }, {});
        setUserSaves(savesMap);

        // Fetch all posts (user's own and saved)
        const userPostsResponse = await fetch(`https://kast-server-713b1c89d478.herokuapp.com/api/auth/user-posts/${uid}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const userPostsData = await userPostsResponse.json();
        const userPosts = userPostsData.posts || [];
        
        // Combine and remove duplicates
        const allPosts = [...userPosts, ...savedPosts];
        const uniquePosts = allPosts.filter((post, index, self) =>
          index === self.findIndex(p => p.id === post.id)
        );
        
        setLinks(uniquePosts);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleUnsavePost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      
      await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/unsave-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ linkId: postId })
      });
      
      // Update local state
      setLinks(prev => prev.filter(link => link.id !== postId));
      setUserSaves(prev => {
        const newSaves = { ...prev };
        delete newSaves[postId];
        return newSaves;
      });
      
      showSnackbar('Post removed from your saves');
    } catch (error) {
      console.error('Error unsaving post:', error);
      showSnackbar('Failed to remove post', 'error');
    }
  };

  const handleDeletePost = async () => {
    try {
      const token = localStorage.getItem('token');
      
      await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/delete-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          postId: postToDelete
        })
      });
      
      // Update local state
      setLinks(prev => prev.filter(link => link.id !== postToDelete));
      setDeleteConfirmOpen(false);
      setPostToDelete(null);
      showSnackbar('Post deleted');
    } catch (error) {
      console.error('Error deleting post:', error);
      showSnackbar('Failed to delete post', 'error');
    }
  };

  const togglePostPrivacy = async (postId, currentPrivacy) => {
    try {
      const token = localStorage.getItem('token');
      
      await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/update-post-privacy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          linkId: postId,
          isPublic: !currentPrivacy
        })
      });
      
      // Update local state
      setLinks(prev => prev.map(link => {
        if (link.id === postId) {
          return {
            ...link,
            isPublic: !currentPrivacy
          };
        }
        return link;
      }));
      
      showSnackbar(`Post made ${!currentPrivacy ? 'public' : 'private'}`);
    } catch (error) {
      console.error('Error updating post privacy:', error);
      showSnackbar('Failed to update privacy', 'error');
    }
  };

  const handleSaveEdit = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      
      await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/update-post-caption', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          linkId: postId,
          caption: editedCaption
        })
      });
      
      // Update local state
      setLinks(prev => prev.map(link => {
        if (link.id === postId) {
          return {
            ...link,
            caption: editedCaption
          };
        }
        return link;
      }));
      
      setEditingPostId(null);
      setEditedCaption('');
      showSnackbar('Caption updated');
    } catch (error) {
      console.error('Error updating post caption:', error);
      showSnackbar('Failed to update caption', 'error');
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const token = localStorage.getItem('token');    
      
      if (userLikes[postId]) {
        await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/unlike-post', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ linkId: postId })
        });
        
        setUserLikes(prev => {
          const newLikes = { ...prev };
          delete newLikes[postId];
          return newLikes;
        });
        
        setLinks(prev => prev.map(link => {
          if (link.id === postId) {
            return {
              ...link,
              likes: Math.max((link.likes || 0) - 1, 0)
            };
          }
          return link;
        }));
        showSnackbar('Post unliked');
      } else {
        await fetch('https://kast-server-713b1c89d478.herokuapp.com/api/auth/like-post', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ linkId: postId })
        });
        
        setUserLikes(prev => ({
          ...prev,
          [postId]: true
        }));
        
        setLinks(prev => prev.map(link => {
          if (link.id === postId) {
            return {
              ...link,
              likes: (link.likes || 0) + 1
            };
          }
          return link;
        }));
        showSnackbar('Post liked');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      showSnackbar('Failed to like post', 'error');
    }
  };

  const fetchLikesForPost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://kast-server-713b1c89d478.herokuapp.com/api/auth/post-likes/${postId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setLikesModal({
          open: true,
          postId,
          likes: data.likes
        });
      }
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds/60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds/3600)}h`;
    return `${Math.floor(seconds/86400)}d`;
  };

  const filteredLinks = links
    .filter(link => {
      // First filter by search term
      const matchesSearch = (
        (link.userName && link.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (link.description && link.description.toLowerCase().includes(searchTerm.toLowerCase())))
      
      // Then filter by view mode
      if (viewMode === 'mySaves') {
        return matchesSearch && link.userId === currentUserId;
      } else {
        return matchesSearch && link.userId !== currentUserId && userSaves[link.id];
      }
    })
    .sort((a, b) => {
      const aIsSaved = a.userId !== currentUserId && userSaves[a.id];
      const bIsSaved = b.userId !== currentUserId && userSaves[b.id];
      
      if (aIsSaved && !bIsSaved) return -1;
      if (!aIsSaved && bIsSaved) return 1;
      
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

  return ( 
    <Container maxWidth="md" sx={{ py: 3 }}>   
      {/* Search Section */}
      <Box sx={{                
        zIndex: 1000,
        py: 2,  
        mt:{xs:'50px', md:'0'}     
      }}>    
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search your saved links"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}            
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: isMobile ? '16px' : '18px',
              borderRadius: 4,
              pr: 6,
               border: '1.5px solid #74706B',
              p: isMobile ? 0 : 1               
            },
            "& fieldset": { 
              border:'none'
            },
            backgroundColor:'white'
          }}
        />
        
        {/* View Mode Toggle */}
        {/* Replace the existing ToggleButtonGroup code with this */}
<Box sx={{ 
  display: 'flex', 
  justifyContent: 'center', 
  alignItems: 'center', 
  mt: 2,  
}}>
  <Typography variant="body1" sx={{ color: viewMode === 'mySaves' ? 'inherit' : '#74706B', fontWeight: viewMode === "mySaves" ? '600' : '500' }}>
    My Saves
  </Typography>
  <FormControlLabel
    control={
      <Switch
        checked={viewMode === 'otherUsers'}
        onChange={(e) => setViewMode(e.target.checked ? 'otherUsers' : 'mySaves')}
        color="default"
        sx={{
          '& .MuiSwitch-thumb': {
            backgroundColor: '#FF890A',
          },
          '& .MuiSwitch-track': {
            backgroundColor: '#FF890A',
            opacity: 0.5,
          },
          '& .Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#FF890A',
            opacity: 0.5,
          },
          '& .Mui-checked .MuiSwitch-thumb': {
            backgroundColor: '#FF890A',
          },
        }}
      />
    }
    label=""
    labelPlacement="bottom"
  />
  <Typography variant="body1" sx={{ color: viewMode === 'otherUsers' ? 'inherit' : '#74706B', fontWeight: viewMode === "otherUsers" ? '600' : '500' }}>
    From Other Users
  </Typography>
</Box>
      </Box>
      
      {isLoading && <LoadingAnimation />}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({...prev, open: false}))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Links List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: isMobile ? 5 : 2 }}>
        {filteredLinks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary">
              {viewMode === 'mySaves' 
                ? "You haven't saved any posts yet"
                : "No posts from other users"}
            </Typography>
          </Box>
        ) : (
          filteredLinks.map((link) => {
            const isOwnPost = link.userId === currentUserId;
            const shouldAnonymize = !link.isPublic && !isOwnPost;
            const displayName = shouldAnonymize ? 'Kast User' : link.userName;
            const avatarSrc = shouldAnonymize 
              ? AvatarIcon
              : link.profilePicture || `https://ui-avatars.com/api/?name=${link.userName}&background=FF890A&color=fff`;

            return (
              <Card 
                key={link.id} 
                sx={{ 
                  borderRadius: 4, 
                  backgroundColor: '#fefaf5', 
                  border: '1px solid #74706B',
                  boxShadow: 'none'
                }}
              >
                <CardContent>
                  {link.image && (
                    <Box
                      component="img"
                      src={link.image}
                      alt="Preview"
                      sx={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'cover',
                        borderRadius: 2,
                        mb: 2,
                      }}
                    />
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}> 
                      {isOwnPost ? 
                        <>
                          {link.isPublic ? 
                            <>
                              <IconButton 
                                onClick={isOwnPost ? () => togglePostPrivacy(link.id, link.isPublic) : undefined}
                                size="small"
                                color= 'primary'
                              >
                                <VisibilityIcon fontSize="large" />
                              </IconButton>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Public
                              </Typography>  
                            </>                           
                            : 
                            <>
                              <IconButton 
                                onClick={isOwnPost ? () => togglePostPrivacy(link.id, link.isPublic) : undefined}
                                size="small"
                                color= 'primary'
                              >
                                <VisibilityOffIcon fontSize="large" />
                              </IconButton>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Private
                              </Typography>  
                            </> 
                          }                                          
                        </> 
                        : 
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              src={avatarSrc}
                              sx={{ width: 32, height: 32, mr:1.5 }}
                            >
                              {displayName.charAt(0)}
                            </Avatar>
                            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                              {displayName}
                            </Typography>
                          </Box>
                        </>
                      }                                           
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {isOwnPost && (
                        <>                                                   
                          {editingPostId === link.id ? (
                            <>
                              <IconButton 
                                onClick={() => handleSaveEdit(link.id)}
                                color="primary"
                                size="small"
                              >
                                <Check fontSize="small" />
                              </IconButton>
                              <IconButton 
                                onClick={() => {
                                  setEditingPostId(null);
                                  setEditedCaption('');
                                }}
                                color="error"
                                size="small"
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton 
                                onClick={() => {
                                  setEditingPostId(link.id);
                                  setEditedCaption(link.caption || '');
                                }}
                                color="primary"
                                size="small"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>

                  <Typography sx={{       
                    fontWeight: 600,
                    fontSize: isMobile ? '15px' : '18px',
                  }}>
                    {link.title || 'No title'}
                  </Typography>
                  
                  {link.description && (
                    <Typography variant="body2" color="text.secondary">
                      {link.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ fontWeight: 800, color:'#74706B', fontSize:'14px'}}
                    >                  
                      {link.siteName}
                    </Typography>
                    {link?.logo && (
                      <Avatar 
                        src={link.logo} 
                        sx={{ width: 40, height: 40 }} 
                      />
                    )}  
                  </Box>
                  
                  {editingPostId === link.id ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={editedCaption}
                      onChange={(e) => setEditedCaption(e.target.value)}
                      placeholder="Add your caption..."
                      size="small"                      
                    />
                  ) : (
                    link.caption && (
                      <Typography variant="body2" sx={{ 
                        fontWeight: 400, 
                        fontSize: "14px", 
                        color: '#5a5753',
                        fontStyle: 'italic'
                      }}>
                        {link.caption}
                      </Typography>
                    )
                  )}                

                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: 5, 
                    height: 48
                  }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => fetchLikesForPost(link.id)}
                    >
                      <IconButton                         
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikePost(link.id);
                        }}
                        sx={{ color: '#115BBA' }} 
                      >
                        {userLikes[link.id] ? (
                          <Favorite color="inherit" sx={{ fontSize: isMobile ? '25px' : '30px' }}/> 
                        ) : (
                          <FavoriteBorder color="inherit" sx={{ fontSize: isMobile ? '25px' : '30px' }}/> 
                        )}
                      </IconButton>
                      <Typography variant="caption" sx={{ fontSize: 10, mt: -0.5 }} onClick={(e) => {
                        e.stopPropagation();
                        fetchLikesForPost(link.id);
                      }}>
                        {link.likes || 0}
                      </Typography>
                    </Box>
                    
                    {isOwnPost ?  
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>                      
                        <IconButton                           
                          onClick={() => {
                            setPostToDelete(link.id);
                            setDeleteConfirmOpen(true);
                          }}
                          sx={{ mt: -1, color: '#115BBA' }}
                        >
                          <Delete sx={{ fontSize: isMobile ? '25px' : '30px' }}/>
                        </IconButton>                      
                      </Box> :  
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>                      
                        <IconButton                           
                          onClick={() => !isOwnPost && handleUnsavePost(link.id)}
                          sx={{ mt: -1, color: '#115BBA' }}
                        >
                          <Bookmark color="inherit" sx={{ fontSize: isMobile ? '25px' : '30px' }}/> 
                        </IconButton>                      
                      </Box>
                    }                   
                    
                    <IconButton                       
                      onClick={() => window.open(link.url, '_blank')}
                      sx={{ mt: -1, color: '#115BBA' }}
                    >
                      <LaunchIcon sx={{ fontSize: isMobile ? '25px' : '30px' }}/>
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>      

      {/* Likes Modal */}
      <Modal open={likesModal.open} onClose={() => setLikesModal({ open: false, postId: null, likes: [] })}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? 300: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Likes</Typography>
          <List>
            {likesModal.likes.length > 0 ? (
              likesModal.likes.map(user => (
                <ListItem key={user.id}>
                  <ListItemAvatar>
                    <Avatar>{user.displayName?.charAt(0) || user.userEmail?.charAt(0)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={user.displayName || user.userEmail.split('@')[0]}                     
                  />
                </ListItem>
              ))
            ) : (
              <Typography variant="body2">No likes yet</Typography>
            )}
          </List>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? 300: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2
        }}>
          <Typography variant="h6" gutterBottom>
            Confirm Delete
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to delete this post? This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error"
              onClick={handleDeletePost}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
      <Layout />
    </Container>
  );
};

export default MySaves;